import { useEffect } from 'react';
import { Link, Navigate, useOutletContext, useRouteLoaderData, useSearchParams } from 'react-router-dom';
import adhocApiProfilerIcon from '../assets/images/home/AdhocApiProfiler.png';
import agentTaskManagerIcon from '../assets/images/home/AgentTaskManager.png';
import apiProfilerIcon from '../assets/images/home/ApiProfiler.png';
import componentsIcon from '../assets/images/home/Components.png';
import computeInsightsIcon from '../assets/images/home/ComputeInsights.png';
import cocoIcon from '../assets/images/home/Coco.png';
import prGuardianIcon from '../assets/images/home/PRGuardian.png';
import untrimmedProfilesIcon from '../assets/images/home/UntrimmedProfiles.png';
import '../styles/HomePage.css';

type UserInfo = {
    isAuditor?: boolean;
};

type HomePageContext = {
    selectedServiceName: string;
};

type Destination = {
    key: string;
    name: string;
    description: string;
    icon: string;
    target: string | null;
    unavailableReason?: string;
};

export function getSafeRedirectTarget(redirectTarget: string | null): string | null {
    const target = redirectTarget?.trim();
    return target?.startsWith('/') && !target.startsWith('//') ? target : null;
}

export function HomePage() {
    const { selectedServiceName } = useOutletContext<HomePageContext>();
    const userInfo = useRouteLoaderData('root') as UserInfo | null;
    const [searchParams] = useSearchParams();
    const redirectTarget = getSafeRedirectTarget(searchParams.get('redir'));

    useEffect(() => {
        window.document.title = 'Regression Detection';
    }, []);

    if (userInfo && redirectTarget) {
        return <Navigate to={redirectTarget} replace />;
    }

    const hasSelectedService = selectedServiceName.trim().length > 0;
    const isSpo = selectedServiceName.toUpperCase() === 'SPO';
    const encodedServiceName = encodeURIComponent(selectedServiceName);
    const serviceTarget = (path: string) =>
        hasSelectedService ? `/service/${encodedServiceName}/${path}` : null;

    const destinations: Destination[] = [
        {
            key: 'prguardian',
            name: 'PR Guardian',
            description: 'See pull request metrics',
            icon: prGuardianIcon,
            target: serviceTarget(userInfo?.isAuditor ? 'allcommits' : 'mycommits'),
            unavailableReason: 'Select a service to view commits',
        },
        {
            key: 'agenttasks',
            name: 'Agent Task Manager',
            description: 'Plan and monitor agent work',
            icon: agentTaskManagerIcon,
            target: serviceTarget('agenttasks'),
            unavailableReason: 'Select a service to manage tasks',
        },
        {
            key: 'apiprofiles',
            name: 'API Profiles',
            description: 'Daily profiles and timelines',
            icon: apiProfilerIcon,
            target: '/dailyapiprofiles',
        },
        {
            key: 'adhocapiprofiler',
            name: 'Ad hoc API Profiler',
            description: 'Generate targeted api profile',
            icon: adhocApiProfilerIcon,
            target: '/adhocapiprofiler',
        },
        {
            key: 'computeinsights',
            name: 'Compute Insights',
            description: 'SPO capacity and efficiency',
            icon: computeInsightsIcon,
            target: isSpo ? '/spocompute' : null,
            unavailableReason: hasSelectedService
                ? 'Available for the SPO service'
                : 'Select the SPO service',
        },
        {
            key: 'profiles',
            name: 'Untrimmed Profiles',
            description: 'Browse full performance profiles',
            icon: untrimmedProfilesIcon,
            target: serviceTarget('profiles'),
            unavailableReason: 'Select a service to browse profiles',
        },
        {
            key: 'components',
            name: 'Components',
            description: 'Explore service components',
            icon: componentsIcon,
            target: serviceTarget('components'),
            unavailableReason: 'Select a service to explore components',
        },
    ];

    return (
        <main className="home-page">
            <div className="home-backdrop" aria-hidden="true" />
            <section className="destination-wheel" aria-label="Regression Detection destinations">
                <div className="wheel-center-brand">
                    <img
                        src={cocoIcon}
                        alt=""
                        aria-hidden="true"
                        className="wheel-center-icon"
                    />
                    <span>CoCo</span>
                </div>
                {destinations.map((destination) => (
                    <DestinationTile key={destination.key} destination={destination} />
                ))}
            </section>
        </main>
    );
}

function DestinationTile({ destination }: { destination: Destination }) {
    const content = (
        <>
            <span className="destination-icon-frame">
                <img src={destination.icon} alt="" className="destination-icon" />
            </span>
            <span className="destination-name">{destination.name}</span>
        </>
    );

    if (!destination.target) {
        return (
            <div
                className={`destination-tile destination-${destination.key} is-disabled`}
                aria-disabled="true"
                aria-label={`${destination.name} unavailable. ${destination.unavailableReason}`}
                title={destination.unavailableReason}
            >
                {content}
            </div>
        );
    }

    return (
        <Link
            className={`destination-tile destination-${destination.key}`}
            to={destination.target}
            aria-label={destination.name}
            title={destination.description}
        >
            {content}
        </Link>
    );
}
